'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useOrderStore } from '@/store/useOrderStore';
import { cn } from '@/lib/utils';
import LinkOrPhotoInput from './LinkOrPhotoInput';

interface Props {
  attempted?: boolean;
}

export default function Step1CustomUpload({ attempted = false }: Props) {
  const { itemInfo, setItemInfo, phone, setPhone } = useOrderStore();
  const [linkUrl, setLinkUrl] = useState(itemInfo.customData?.linkUrl || '');
  const [description, setDescription] = useState(itemInfo.customData?.description || '');
  const [imageUrls, setImageUrls] = useState<string[]>(itemInfo.customData?.imageUrls || []);

  const updateCustomData = (data: Partial<{ imageUrls: string[]; linkUrl: string; description: string }>) => {
    const newCustomData = {
      imageUrls: data.imageUrls ?? imageUrls,
      linkUrl: data.linkUrl ?? linkUrl,
      description: data.description ?? description,
    };
    setItemInfo({
      isCustom: true,
      itemId: null,
      category: '커스텀 물품',
      basePrice: 0,
      options: {},
      customData: newCustomData,
    });
  };

  const handleLinkChange = (value: string) => {
    setLinkUrl(value);
    updateCustomData({ linkUrl: value });
  };

  const handleImagesChange = (urls: string[]) => {
    setImageUrls(urls);
    updateCustomData({ imageUrls: urls });
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    updateCustomData({ description: value });
  };

  return (
    <div className="space-y-4">
      {/* 설명 */}
      <div className="space-y-2">
        <Label>
          물품 설명
          <span className="text-red-500 ml-0.5">*</span>
        </Label>
        <Textarea
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder={"물품의 종류와 크기(폭, 높이, 등)를 알려주시면 정확한 견적이 가능해요.\n재질, 특이사항 등도 적어주세요."}
          maxLength={500}
          rows={4}
          className={cn(attempted && !description.trim() && 'border-red-500 ring-2 ring-red-200')}
        />
        <div className="text-xs text-gray-400 text-right">{description.length}/500</div>
        {attempted && !description.trim() && (
          <p className="text-sm text-red-500">물품 설명을 입력해주세요</p>
        )}
      </div>

      <LinkOrPhotoInput
        linkUrl={linkUrl}
        imageUrls={imageUrls}
        onLinkChange={handleLinkChange}
        onImagesChange={handleImagesChange}
        attempted={attempted}
        required
      />

      {/* 전화번호 */}
      <div className="space-y-2">
        <Label>
          연락처 (견적 안내 문자 수신용)
          <span className="text-red-500 ml-0.5">*</span>
        </Label>
        <Input
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) => {
            const numbers = e.target.value.replace(/\D/g, '');
            let formatted = numbers;
            if (numbers.length <= 3) formatted = numbers;
            else if (numbers.length <= 7) formatted = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            else formatted = `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
            setPhone(formatted);
          }}
          placeholder="010-0000-0000"
          maxLength={13}
          className={cn(
            attempted && !/^01[016789]-\d{3,4}-\d{4}$/.test(phone) && 'border-red-500 ring-2 ring-red-200',
          )}
        />
        {phone && !/^01[016789]-\d{3,4}-\d{4}$/.test(phone) && (
          <p className="text-sm text-red-500">올바른 휴대폰 번호를 입력해주세요</p>
        )}
        {attempted && !phone && (
          <p className="text-sm text-red-500">연락처를 입력해주세요</p>
        )}
      </div>

      {/* 안내문 */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          문의하신 물품은 운송매니저 확인 후 배송비 안내와 함께 신청링크를 문자로 전달 드립니다.
        </p>
      </div>
    </div>
  );
}
